import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { TabRowCloseButton } from "@/components/ui/glass";
import { cn } from "@/lib/utils";
import { usePortalContainer } from "@/contexts/portal-container";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" modal={false} {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  container,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  const portalContainer = usePortalContainer();
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      container={container ?? portalContainer}
      {...props}
    />
  );
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogContent({
  className,
  containerClassName,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  /** Classes for the positioning wrapper (width, height): the box inside is what scrolls. */
  containerClassName?: string;
  showCloseButton?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      {/* Dialogs are the top layer of the widget: above the panel (z-9999) AND
          above the full-screen prize-reveal window (z-10010), which itself
          opens dialogs (the reveal's "+N more games" chip) — a dialog launched
          from inside the reveal has to cover it, not hide behind it. */}
      <div
        data-slot="dialog-backdrop"
        className="pointer-events-auto fixed inset-0 z-10020 bg-black/50"
      />
      {/* Two boxes: the positioned wrapper (no overflow, so the close can hang past the corner
          the way the panel's does) and, inside it, the glass surface that scrolls. Callers'
          `className` lands on the surface, as before; size the wrapper via `containerClassName`. */}
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "pointer-events-auto fixed top-[50%] left-[50%] z-10021 grid max-h-[calc(100%-32px)] w-full max-w-[calc(100%-32px)] translate-x-[-50%] translate-y-[-50%] font-body duration-200 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:max-w-xl lg:max-h-[80vh]",
          containerClassName
        )}
        {...props}
      >
        <div
          data-slot="dialog-surface"
          className={cn(
            // Same surface language as the cards and the panel: glass fill + gradient rim, lifted.
            "glass-card panel-lift-shadow flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-y-contain rounded-2xl p-6",
            className
          )}
        >
          {children}
        </div>
        {showCloseButton && (
          // The panel's close, one level up: the same glass circle hanging 8px past the top and
          // right edges of the dialog.
          <DialogPrimitive.Close asChild>
            <TabRowCloseButton className="absolute -top-2 -right-2 z-10" />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-heading text-base leading-none font-medium",
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("font-body text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
